import type { HomeSectionTitleData } from "@/lib/homepage";

type SectionTitleProps = {
  data?: HomeSectionTitleData | null;
  icon?: string;
  FadeMiniTitle?: string;
  IconMiniTitle?: string;
  title?: string;
  descriptionBold?: string;
  description?: string;
  buttonText?: string;
  buttonHref?: string;
  buttonAction?: () => void;
};

const BUTTON_BASE_CLASS =
  "flex h-[45px] px-6 justify-center items-center gap-1.5 bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl pl-6 pr-4 py-3 h-12 font-semibold text-lg cursor-pointer";

export default function SectionTitle(props: SectionTitleProps = {}) {
  const {
    data,
    icon,
    FadeMiniTitle,
    IconMiniTitle,
    title,
    descriptionBold,
    description,
    buttonText,
    buttonHref,
    buttonAction,
  } = props;

  const resolvedIcon = data?.icon ?? icon;
  const resolvedIconMiniTitle = data?.iconMiniTitle ?? IconMiniTitle;
  const resolvedFadeMiniTitle = data?.fadeMiniTitle ?? FadeMiniTitle;
  const resolvedTitle = data?.title ?? title;
  const resolvedDescriptionBold = data?.descriptionBold ?? descriptionBold;
  const resolvedDescription = data?.description ?? description;
  const resolvedButtonText = data?.ctaLabel ?? buttonText;
  const resolvedButtonHref = data?.ctaUrl ?? buttonHref;

  const hasButtonHref = Boolean(resolvedButtonHref);
  const hasButtonAction = typeof buttonAction === "function";
  const shouldRenderButton = Boolean(resolvedButtonText && (hasButtonHref || hasButtonAction));

  return (
    <section className="relative flex flex-col items-center bg-onplexus-dark overflow-hidden">
      {/* Content Container */}
      <div className="relative w-full flex flex-col items-center gap-4 px-4 pb-0 pt-12 max-[599px]:px-4 min-[600px]:max-[767px]:px-6 min-[768px]:px-8">
        <div className="flex flex-col items-center gap-3 pb-5">
          {/* Mini Title with Icon - Only render if icon or miniTitle exists */}
          {(resolvedIcon || resolvedIconMiniTitle) && (
            <div className="flex items-center gap-2">
              {resolvedIcon && (
                <div className="flex w-8 h-8 justify-center items-center">
                  <span className="text-onplexus-gold-bright onplexus-icon text-[2.8rem] pt-0.5">
                    {resolvedIcon}
                  </span>
                </div>
              )}
              {resolvedIconMiniTitle && (
                <span className="title-mini font-bold">
                  {resolvedIconMiniTitle}
                </span>
              )}
            </div>
          )}
          {/* Fade Mini Title */}
          {resolvedFadeMiniTitle && (
            <div className="flex items-center justify-center gap-3">
              <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
              <span className="title-mini">{resolvedFadeMiniTitle}</span>
              <div className="h-0.5 w-24 bg-gradient-to-l from-transparent via-onplexus-gold-bright to-onplexus-gold"></div>
            </div>
          )}
        </div>

        {/* H2 Title - Only render if title exists */}
        {resolvedTitle && (
          <h2 className="w-full text-white text-3xl text-center leading-[48px] font-normal">
            {resolvedTitle}
          </h2>
        )}

        {/* Description - Only render if description exists */}
        {(resolvedDescriptionBold || resolvedDescription) && (
          <div className="flex flex-col max-w-[550px] gap-4 w-full">
            {resolvedDescriptionBold && (
              <p className="w-full text-white text-center text-2xl font-semibold leading-[26px]">
                {resolvedDescriptionBold}
              </p>
            )}
            {resolvedDescription && (
              <p className="w-full text-white/70 text-center text-lg leading-[26px] font-normal">
                {resolvedDescription}
              </p>
            )}
          </div>
        )}

        {/* CTA Button - Only render if content and behaviour exist */}
        {shouldRenderButton &&
          (hasButtonHref ? (
            <a href={resolvedButtonHref} className={`${BUTTON_BASE_CLASS} no-underline`}>
              <span>{resolvedButtonText}</span>
              <span className="onplexus-icon text-5xl pt-0.5">→</span>
            </a>
          ) : (
            <button type="button" onClick={buttonAction} className={BUTTON_BASE_CLASS}>
              <span>{resolvedButtonText}</span>
              <span className="onplexus-icon text-5xl pt-0.5">→</span>
            </button>
          ))}
      </div>
    </section>
  );
}
